import {addMinutes} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {hasIn} from 'lodash';
import {
  IAppointmentSpots,
  IAvailableSpot,
  IBlockAppointment,
  IDateMetadata,
  IGeneratedBlocks,
  IRangeBlock,
  ISpot
} from '../../interfaces/date';

export class DateHelper {
  public static maxSystemSpot = 1440;
  public timeZone: string;
  constructor(timeZone?: string) {
    this.timeZone = timeZone ? timeZone : 'GMT';
  }


  public static getSystem(hours: number, minutes: number): number {
    return (hours * 60) + minutes;
  }



  public static checkBlocking(timeBlocks: IRangeBlock[]): [IRangeBlock, number][] {

    // Safe return for next reduce operation;
    if (timeBlocks.length === 0) { return []; }

    timeBlocks.sort((a, b) => DateHelper.getSystem(a.start.hour, a.start.minute) - DateHelper.getSystem(b.start.hour, b.start.minute));
    // Block, Original Index for search
    const invalidRanges: [IRangeBlock, number][] = [];

    timeBlocks.reduce((prev, current, index) => {
      const prevStartSystem = DateHelper.getSystem(prev.start.hour, prev.start.minute);
      const prevEndSystem = DateHelper.getSystem(prev.end.hour, prev.end.minute);
      const currentStartSystem = DateHelper.getSystem(current.start.hour, current.start.minute);
      const currentEndSystem = DateHelper.getSystem(current.end.hour, current.end.minute);
      if (currentStartSystem <= prevEndSystem) {
        // Blocking stage
        invalidRanges.push([prev, index - 1]);
      }
      return current;
    });


    return invalidRanges;

  }

  public static generateBlockAppointment(dateStart: Date, minutes: number, timeZone: string): IBlockAppointment {
    const dateEnd = addMinutes(new Date(dateStart.toISOString()), minutes);
    const [timestampStart, timestampEnd] = [dateStart.getTime(), dateEnd.getTime()];
    if (timestampEnd < timestampStart) { throw new Error('The initial date cannot be greater than the final date.'); }

    return {
      timeZone,
      startDateUTC: {
        date: dateStart,
        day: dateStart.getUTCDate(),
        hour: dateStart.getUTCHours(),
        minute: dateStart.getUTCMinutes(),
        month: dateStart.getUTCMonth(),
        year: dateStart.getUTCFullYear(),
        timestamp: dateStart.getTime(),
        system: DateHelper.getSystem(dateStart.getUTCHours(), dateStart.getUTCMinutes())
      },
      endDateUTC: {
        date: dateEnd,
        day: dateEnd.getUTCDate(),
        hour: dateEnd.getUTCHours(),
        minute: dateEnd.getUTCMinutes(),
        month: dateEnd.getUTCMonth(),
        year: dateEnd.getUTCFullYear(),
        timestamp: dateEnd.getTime(),
        system: DateHelper.getSystem(dateEnd.getUTCHours(), dateEnd.getUTCMinutes())
      },
      minuteBlock: minutes
    };
  }

  /**
   * Convert UTC time to Client time
   * @param date date
   * @param timeZone timeZone to convert
   */
  public static convertUtcToZonedTime(date: Date, timeZone?: string): Date {
    try {
      return utcToZonedTime(date, timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch (ex) {
      return utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }


  generateSpots(minutesBlocks: number, dateUTC: Date): IGeneratedBlocks {
    const [ day, month, year, hours, minutes ] =
      [ dateUTC.getUTCDate(), dateUTC.getUTCMonth(), dateUTC.getUTCFullYear(), dateUTC.getUTCHours(), dateUTC.getUTCMinutes() ];
    let system = DateHelper.getSystem(hours, 0);
    const spots: ISpot[] = [];
    while ((system + minutesBlocks) <= DateHelper.maxSystemSpot) {
      spots.push({startSystem: system, endSystem: system + minutesBlocks});
      system = system + minutesBlocks;
    }
    return {day, month, year, date: dateUTC, timeZone: 'GMT', spots, timestamp: dateUTC.getTime()};
  }


  generateZonedSpots(minutesBlocks: number, date: Date): IGeneratedBlocks {
    const zonedDate = utcToZonedTime(date.toISOString(), this.timeZone);
    const [ day, month, year, hours, minutes ] = [ zonedDate.getDate(),
      zonedDate.getMonth(), zonedDate.getFullYear(), zonedDate.getHours(), zonedDate.getMinutes() ];
    let system = DateHelper.getSystem(hours, 0);
    const spots: ISpot[] = [];
    while ((system + minutesBlocks) <= DateHelper.maxSystemSpot) {
      spots.push({startSystem: system, endSystem: system + minutesBlocks});
      system = system + minutesBlocks;
    }
    return {day, month, year, date: zonedDate, timeZone: this.timeZone, spots, timestamp: zonedDate.getTime()};
  }



  generateAvailableSpotsByObject(appointments: IBlockAppointment[], generatedBlocks: IGeneratedBlocks): IAppointmentSpots {
    let spots: ISpot[] | IAvailableSpot[] = [...generatedBlocks.spots];
    appointments.forEach((appointment) => {
      const appointmentStart = this.convertToZonedDateMetadata(appointment.startDateUTC);
      const appointmentEndSystem = appointmentStart.system + appointment.minuteBlock;

      spots = (spots as any).reduce((prev: any, current: ISpot) => {
        if (hasIn(current, 'available') && (current as IAvailableSpot).available === false) {
          return [...prev, current];
        }
        if ((appointmentStart.system >= current.startSystem && appointmentStart.system < current.endSystem) ||
          (appointmentEndSystem <= current.endSystem && appointmentEndSystem > current.startSystem) ||
          (current.startSystem  >= appointmentStart.system  && current.endSystem <= appointmentEndSystem)
        ) {
          return [...prev, {...current, available: false}];
        }
        return [...prev, {...current, available: true}];

      }, []);
    });


    return {...generatedBlocks, spots} as IAppointmentSpots;
  }


  convertToZonedDateMetadata(metadata: IDateMetadata): IDateMetadata {
    const date = utcToZonedTime(metadata.date.toISOString(), this.timeZone);
    return {
      date,
      hour: date.getHours(),
      day: date.getDate(),
      minute: date.getMinutes(),
      month: date.getMonth(),
      timestamp: date.getTime(),
      year: date.getFullYear(),
      system: DateHelper.getSystem(date.getHours(), date.getMinutes())
    };
  }

  /**
   * Convert ISOString to DateMetadata
   * @param date fecha
   */
  convertISOStringToDateMetadata(date: Date): IDateMetadata {
    return {
      date,
      hour: date.getHours(),
      day: date.getDate(),
      minute: date.getMinutes(),
      month: date.getMonth(),
      timestamp: date.getTime(),
      year: date.getFullYear(),
      system: DateHelper.getSystem(date.getHours(), date.getMinutes())
    };
  }
}
